import React, { useRef, useState, useEffect, useCallback } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Input, Button, Space, Table, Modal, message, Spin, Switch } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./browser_sales_order_invoice.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { convertStatusToString, STATUS_LIST } from "../../components/hoc_so_invoice";
import useSWR from 'swr';

interface DataType {
    key: number;
    trans_id?: string;
    invoice_no: string;
    invoice_date: string;
    invoice_due_date: string;
    sales_order_no: string;
    po_customer_no: string;
    packing_list_no: string;
    customer: string;
    name_bill: string;
    remark: string;
    invoice_amount: number;
    status: string;
    dealer_re_code?: string;
    warehouse_name?: string;
}

type DataIndex = keyof DataType;

const BrowserSalesOrderInvoice: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sorter, setSorter] = useState<{ field?: string; order?: 'ascend' | 'descend' }>({});
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [isNavigating, setIsNavigating] = useState(false);
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [showAllWarehouse, setShowAllWarehouse] = useState<boolean>(false);
    const hasWarehouseAuthority = userData?.otoritas ? userData.otoritas.split(';').map((val: string) => val.trim()).includes('77') : false;

    const fetcher = async (url: string) => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    };

    const queryParams = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        ...(sorter.field && { sortField: sorter.field, sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc' }),
        ...((userData?.warehouse_code && !showAllWarehouse) && { warehouse_code: userData.warehouse_code })
    });

    Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key][0]) {
            queryParams.append(`filter_${key}`, filters[key][0]);
        }
    });

    const { data: serverData, isValidating } = useSWR(
        `${process.env.REACT_APP_BACKEND_URL}/api/so-invoice/all?${queryParams.toString()}`,
        fetcher,
        { keepPreviousData: true }
    );

    const data = React.useMemo(() => {
        if (!serverData || !serverData.data) return [];
        return serverData.data.map((item: any) => ({
            key: item.id,
            trans_id: item.trans_id,
            invoice_no: item.invoice_no,
            invoice_date: item.invoice_date,
            invoice_due_date: item.invoice_due_date,
            sales_order_no: item.sales_order_no,
            po_customer_no: item.po_customer_no,
            packing_list_no: item.packing_list_no,
            customer: item.customer,
            name_bill: item.name_bill,
            remark: item.remark,
            invoice_amount: parseFloat(item.invoice_amount),
            status: String(item.status),
            dealer_re_code: item.dealer_re_code,
            warehouse_name: item.warehouse_name,
        }));
    }, [serverData]);

    const totalCount = serverData?.total || 0;
    const loading = isValidating;

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        filteredValue: filters[dataIndex] || null,
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) => (
            <span style={{ whiteSpace: 'nowrap' }}> { }
                {searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                )}
            </span>
        ),
    });

    const warehouseFilters = React.useMemo(() => {
        const unique = new Map<string, string>();
        data.forEach((item: DataType) => {
            if (item.dealer_re_code) {
                unique.set(item.dealer_re_code, item.warehouse_name || item.dealer_re_code);
            }
        });
        return Array.from(unique.entries()).map(([value, text]) => ({ text, value }));
    }, [data]);

    const baseColumns: TableColumnsType<DataType> = [
        {
            title: 'Invoice No',
            dataIndex: 'invoice_no',
            key: 'invoice_no',
            width: '10%',
            ...getColumnSearchProps('invoice_no'),
            sorter: true,
        },
        {
            title: 'Date',
            dataIndex: 'invoice_date',
            key: 'invoice_date',
            width: '10%',
            ...getColumnSearchProps('invoice_date'),
            sorter: true,
        },
        {
            title: 'Due Date',
            dataIndex: 'invoice_due_date',
            key: 'invoice_due_date',
            width: '10%',
            ...getColumnSearchProps('invoice_due_date'),
            sorter: true,
        },
        {
            title: 'Sales Order No',
            dataIndex: 'sales_order_no',
            key: 'sales_order_no',
            width: '10%',
            ...getColumnSearchProps('sales_order_no'),
            sorter: true,
        },
        {
            title: 'PO Customer No',
            dataIndex: 'po_customer_no',
            key: 'po_customer_no',
            width: '10%',
            ...getColumnSearchProps('po_customer_no'),
            sorter: true,
        },
        {
            title: 'Packing List No',
            dataIndex: 'packing_list_no',
            key: 'packing_list_no',
            width: '10%',
            ...getColumnSearchProps('packing_list_no'),
            sorter: true,
        },
        {
            title: 'Customer',
            dataIndex: 'customer',
            key: 'customer',
            width: '15%',
            ...getColumnSearchProps('customer'),
            sorter: true,
        },
        {
            title: 'Remark',
            dataIndex: 'remark',
            key: 'remark',
            width: '15%',
            ...getColumnSearchProps('remark'),
            sorter: true,
        },
        {
            title: 'Total',
            dataIndex: 'invoice_amount',
            key: 'invoice_amount',
            width: '10%',
            sorter: true,
            render: (text: number) => new Intl.NumberFormat('id-ID', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(text),
        },
        {
            title: 'Warehouse',
            dataIndex: 'dealer_re_code',
            key: 'dealer_re_code',
            width: '10%',
            filters: warehouseFilters,
            filteredValue: filters['dealer_re_code'] || null,
            onFilter: (value, record) => record.dealer_re_code === value,
            sorter: true,
            render: (text: string, record: DataType) => {
                const displayLabel = record.warehouse_name || text || '';
                return (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {displayLabel}
                    </span>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '10%',
            filters: STATUS_LIST.map(item => ({ text: item.label, value: String(item.id) })),
            filterMultiple: false,
            filteredValue: filters['status'] || null,
            onFilter: (value, record) => record.status === value,
            render: (text: string) => {
                const displayLabel = convertStatusToString(text);
                return (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {displayLabel}
                    </span>
                );
            },
        },
    ];

    const columns: TableColumnsType<DataType> = [...baseColumns];
    const handleTableChange = (
        pagination: any,
        tblFilters: any,
        tblSorter: any
    ) => {
        setPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
        setFilters(tblFilters || {});
        setSorter({
            field: tblSorter.field,
            order: tblSorter.order,
        });
    };
    return (
        <Spin spinning={loading || isNavigating} size="large" tip="Please Wait...">
            <div id="iet2" style={transformStyle}>
                <div className="gjs-grid-row" id="icul">
                    <div className="gjs-grid-column" id="i4sh">
                        <Breadcrumb
                            items={[
                                {
                                    title: <HomeOutlined />,
                                },
                                {
                                    title: (
                                        <>
                                            <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Sales Order Invoice</span>
                                        </>
                                    ),
                                },
                                {
                                    href: "#/dashboard/sales_order_invoice",
                                    title: "Sales Order Invoice",
                                },
                            ]}
                        />
                    </div>
                </div>
                <div className="gjs-grid-row" id="igs5">
                    <div className="gjs-grid-column" id="igcv">
                        {hasWarehouseAuthority && (
                            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                                <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                            </div>
                        )}
                        <Table<DataType>
                            columns={columns}
                            dataSource={data}
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: totalCount,
                                showSizeChanger: true,
                            }}
                            loading={loading}
                            onChange={handleTableChange}
                            onRow={(record) => {
                                return {
                                    onDoubleClick: async () => {
                                        if (isNavigating) return;
                                        setIsNavigating(true);
                                        try {
                                            const identifier = record.invoice_no || record.trans_id;
                                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/so-invoice/details/${identifier}`);
                                            if (!response.ok) {
                                                throw new Error(`HTTP error! status: ${response.status}`);
                                            }
                                            const invoiceDetails = await response.json();
                                            localStorage.setItem('salesOrderInvoiceData', JSON.stringify(invoiceDetails));
                                            navigate('/dashboard/sales_order_invoice');
                                        } catch (error) {
                                            console.error('Failed to fetch invoice details:', error);
                                            setIsNavigating(false);
                                        }
                                    },
                                };
                            }}
                        />

                    </div>
                </div>
            </div>
        </Spin>
    );
};

export default BrowserSalesOrderInvoice;
